import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { onInstallable, promptInstall } from "@/lib/sw-register";
import { toast } from "sonner";

/**
 * Renders nothing until the browser fires `beforeinstallprompt`.
 * Then shows a subtle "Install app" pill that triggers the native install flow.
 *
 * `variant`:
 *   - "floating" — fixed bottom-right pill (default, used on Landing)
 *   - "inline"   — plain button to embed in a header / menu
 */
export default function InstallAppButton({ variant = "floating", className = "" }) {
  const [canInstall, setCanInstall] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => onInstallable(setCanInstall), []);

  if (!canInstall) return null;

  const handleClick = async () => {
    setBusy(true);
    try {
      const res = await promptInstall();
      if (res.outcome === "accepted") {
        toast.success("Value Pool added to your home screen");
      }
    } finally { setBusy(false); }
  };

  if (variant === "inline") {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={busy}
        className={className}
        data-testid="pwa-install-inline"
      >
        <Download className="w-3.5 h-3.5 mr-1.5" /> Install app
      </Button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`fixed bottom-5 right-5 z-50 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-shadow px-4 py-2.5 flex items-center gap-2 text-sm font-medium ${className}`}
      data-testid="pwa-install-floating"
    >
      <Download className="w-4 h-4" />
      {busy ? "Installing…" : "Install Value Pool"}
    </button>
  );
}
